import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Typography } from "@mui/material";
import Shell from "./layouts/Shell";
import Routes from "./routes/Routes";
import {
  hasStoredAuthToken,
  getAuthProfileFromToken,
  getAuthUserIdFromToken,
  redirectToLogin,
  persistLaunchContextFromLocation,
} from "./utils/authSession";
import { signIn, updateUser } from "./store/auth.slice";
import { apiGetUser, apiTrackLogin } from "./graphQL/user";
import { apiGetDomainByName, apiUpdateDomainCompanyKnowledge } from "./graphQL/domain";
import { saveCompanyKnowledge } from "./utils/voiceProfileDB";
import COMPANY_KNOWLEDGE_ROUSS_CAPITAL from "./data/companyKnowledgeRoussCapital";

const App = () => {
  const dispatch = useDispatch();
  const authenticated = useSelector((state) => state.auth.authenticated);
  const [redirecting, setRedirecting] = useState(false);

  const hasStoredToken = hasStoredAuthToken();
  const shouldRedirectToHostedLogin = !authenticated && !hasStoredToken;

  // Persist launch context on mount
  useEffect(() => {
    persistLaunchContextFromLocation(window.location);
  }, []);

  // Redirect to login if no token
  useEffect(() => {
    if (!shouldRedirectToHostedLogin) return;
    setRedirecting(true);
    redirectToLogin();
  }, [shouldRedirectToHostedLogin]);

  // Hydrate auth from stored token
  useEffect(() => {
    if (!hasStoredToken) return;
    if (authenticated) return;

    const userId = getAuthUserIdFromToken();
    const profile = getAuthProfileFromToken();

    if (userId) {
      dispatch(
        signIn({
          userId,
          name: profile?.name || profile?.email || userId,
          email: profile?.email || "",
        })
      );

      // Fetch user record from database to get domain.
      // Prefer the JWT's email claim — when Cognito issues a sub UUID that
      // isn't the mongo _id, looking up by email matches the user record's
      // email/name/username fields (case-insensitive on the backend).
      const lookupKey = profile?.email || userId;
      apiGetUser(lookupKey)
        .then((dbUser) => {
          if (dbUser) {
            const role = String(dbUser.role || "").toLowerCase();
            const domainList = Array.isArray(dbUser.domainList) ? dbUser.domainList : [];
            const hasMultiDomainAccess =
              domainList.length > 1 ||
              domainList.some((d) => String(d).toLowerCase() === "all");
            const isSuperAdmin =
              dbUser?.isSuperAdmin === true ||
              role === "superadmin" ||
              role === "super_admin" ||
              role === "sysadmin" ||
              role.includes("super") ||
              hasMultiDomainAccess;

            let initialDomain = dbUser.domain || null;
            if (isSuperAdmin) {
              try {
                const saved = localStorage.getItem("cs_selected_domain");
                if (saved !== null) initialDomain = saved || null;
              } catch {}
            }
            dispatch(
              updateUser({
                domain: initialDomain,
                role: dbUser.role || null,
                domainList,
                data: dbUser.data || null,
                isSuperAdmin,
              })
            );

            // Record the login once per genuine sign-in. handleAuthRedirect()
            // sets LOGIN_ACTION=1 only when a fresh OAuth token was just
            // received, so page refreshes (same stored token) do not re-count.
            try {
              if (localStorage.getItem("LOGIN_ACTION") === "1") {
                localStorage.removeItem("LOGIN_ACTION");
                apiTrackLogin();
              }
            } catch {}

            // Fetch domain company knowledge and seed to local IndexedDB
            if (dbUser.domain) {
              apiGetDomainByName(dbUser.domain)
                .then((domainRecord) => {
                  if (domainRecord?.companyKnowledgeBase) {
                    saveCompanyKnowledge(domainRecord.companyKnowledgeBase);
                    dispatch(updateUser({ domainId: domainRecord.id }));
                  } else if (domainRecord && String(domainRecord.name || "").toLowerCase() === "rouss") {
                    apiUpdateDomainCompanyKnowledge(domainRecord.id, COMPANY_KNOWLEDGE_ROUSS_CAPITAL)
                      .then((updated) => {
                        const text = updated?.companyKnowledgeBase || COMPANY_KNOWLEDGE_ROUSS_CAPITAL;
                        saveCompanyKnowledge(text);
                        dispatch(updateUser({ domainId: domainRecord.id }));
                      })
                      .catch(() => {
                        saveCompanyKnowledge(COMPANY_KNOWLEDGE_ROUSS_CAPITAL);
                        dispatch(updateUser({ domainId: domainRecord.id }));
                      });
                  } else if (domainRecord) {
                    dispatch(updateUser({ domainId: domainRecord.id }));
                  }
                })
                .catch((err) => {
                  console.error("Failed to fetch domain knowledge:", err);
                });
            }
          }
        })
        .catch((err) => {
          console.error("Failed to fetch user profile:", err);
        });
    } else {
      // Token exists but can't parse userId — redirect to login
      setRedirecting(true);
      redirectToLogin();
    }
  }, [hasStoredToken, authenticated, dispatch]);

  if (redirecting || shouldRedirectToHostedLogin) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <CircularProgress size={24} />
        <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Redirecting to login...</Typography>
      </Box>
    );
  }

  if (!authenticated) {
    return (
      <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Shell>
      <Routes />
    </Shell>
  );
};

export default App;
